import { read, utils, writeFile, type WorkBook } from "xlsx";
import type { CensusImportRow, ExpectedDebitsBandDraft, ModificationReportRow, RelationClass } from "../types";
import { RELATIONS } from "../types";

type SheetCell = string | number | boolean | Date | null | undefined;
type SheetRow = Record<string, SheetCell>;

function cellText(value: SheetCell): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const text = String(value).trim();
  return text === "" ? null : text;
}

function cellNumber(value: SheetCell): number | null {
  const text = cellText(value);
  if (text === null) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function readColumn(row: SheetRow, ...names: string[]): SheetCell {
  const keys = Object.keys(row);
  for (const name of names) {
    const key = keys.find((candidate) => candidate.trim().toLowerCase() === name.toLowerCase());
    if (key !== undefined) return row[key];
  }
  return undefined;
}

function normalizeDate(value: SheetCell): string | null {
  const text = cellText(value);
  if (text === null) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return text;
}

function validateCensusRow(row: Omit<CensusImportRow, "errors">): string[] {
  const errors: string[] = [];
  if (row.name.trim() === "") errors.push("Name is required.");
  if (!(RELATIONS as readonly string[]).includes(row.relation)) errors.push(`Relation "${row.relation}" must be one of ${RELATIONS.join(", ")}.`);
  if (row.gender !== null && row.gender !== "M" && row.gender !== "F") errors.push("Gender must be M or F.");
  if (row.dateOfBirth !== null && !/^\d{4}-\d{2}-\d{2}$/.test(row.dateOfBirth)) errors.push("DOB must be a date.");
  return errors;
}

/** Reads the `MUGS` tab of a Census Creator workbook. Returns null when the tab is missing. */
export async function parseCensusWorkbook(file: File): Promise<CensusImportRow[] | null> {
  const workbook = read(await file.arrayBuffer(), { type: "array", cellDates: true });
  const sheet = workbook.Sheets["MUGS"];
  if (!sheet) return null;
  const rows = utils.sheet_to_json<SheetRow>(sheet, { defval: null });
  return rows.map((row, index) => {
    const draft: Omit<CensusImportRow, "errors"> = {
      rowNumber: index + 2,
      name: cellText(readColumn(row, "Name")) ?? "",
      externalId: cellText(readColumn(row, "ID")),
      relation: cellText(readColumn(row, "Relation")) ?? "",
      gender: cellText(readColumn(row, "Gender", "Sex"))?.toUpperCase().charAt(0) ?? null,
      dateOfBirth: normalizeDate(readColumn(row, "DOB", "Date of Birth")),
      heightFeet: cellNumber(readColumn(row, "Height ft", "Height Ft", "Ft")),
      heightInches: cellNumber(readColumn(row, "Height in", "Height In", "In")),
      weightLbs: cellNumber(readColumn(row, "Weight", "Weight lbs")),
      ssn: cellText(readColumn(row, "SSN")),
      notes: cellText(readColumn(row, "Notes")),
    };
    return { ...draft, errors: validateCensusRow(draft) };
  });
}

const SHEET_TO_CLASS: Record<string, RelationClass> = {
  applicant: "Applicant",
  employee: "Applicant",
  spouse: "SpouseOrDependentAdult",
  "spouse and dependent adult": "SpouseOrDependentAdult",
  "spouse/dependent adult": "SpouseOrDependentAdult",
  child: "Child",
};

/** The header row the upload reads, in the order the editor shows the columns. */
export const EXPECTED_DEBITS_TEMPLATE_COLUMNS = ["Age Start", "Age End", "Male Expected", "Male Base", "Female Expected", "Female Base", "Composite Expected", "Composite Base"] as const;

/** One sheet per relation class, named the way the parser expects and the actuaries say it. */
export const EXPECTED_DEBITS_TEMPLATE_SHEETS = ["Employee", "Spouse", "Child"] as const;

export const EXPECTED_DEBITS_TEMPLATE_FILE_NAME = "expected-debits-template.xlsx";

/**
 * Feedback 8.1: the workbook an actuary fills in and uploads. Three band sheets with the header row only, plus a README
 * sheet the parser ignores (its name is not a relation class) that says what goes where.
 */
export function buildExpectedDebitsTemplate(): WorkBook {
  const workbook = utils.book_new();
  for (const sheetName of EXPECTED_DEBITS_TEMPLATE_SHEETS) {
    const sheet = utils.aoa_to_sheet([[...EXPECTED_DEBITS_TEMPLATE_COLUMNS]]);
    sheet["!cols"] = EXPECTED_DEBITS_TEMPLATE_COLUMNS.map((column) => ({ wch: column.length + 4 }));
    utils.book_append_sheet(workbook, sheet, sheetName);
  }
  const readme = utils.aoa_to_sheet([
    ["Expected Debits upload template"],
    [],
    ["One row per age band on each of the Employee, Spouse, and Child sheets. Spouse covers dependent adults too."],
    ["Age Start and Age End are whole years, inclusive; bands must start at 0 and run contiguously with no overlaps."],
    ["Expected and Base are debits for that band: Male, Female, and Composite."],
    ["Leave this README sheet in place or delete it; the upload only reads the three band sheets."],
  ]);
  readme["!cols"] = [{ wch: 110 }];
  utils.book_append_sheet(workbook, readme, "README");
  return workbook;
}

export function downloadExpectedDebitsTemplate(): void {
  writeFile(buildExpectedDebitsTemplate(), EXPECTED_DEBITS_TEMPLATE_FILE_NAME);
}

/** Reads an Expected Debits workbook with Applicant, Spouse, and Child sheets. */
export async function parseExpectedDebitsWorkbook(file: File): Promise<ExpectedDebitsBandDraft[]> {
  const workbook = read(await file.arrayBuffer(), { type: "array" });
  const bands: ExpectedDebitsBandDraft[] = [];
  for (const sheetName of workbook.SheetNames) {
    const relationClass = SHEET_TO_CLASS[sheetName.trim().toLowerCase()];
    if (!relationClass) continue;
    const rows = utils.sheet_to_json<SheetRow>(workbook.Sheets[sheetName], { defval: null });
    for (const row of rows) {
      bands.push({
        relationClass,
        ageStart: cellNumber(readColumn(row, "Age Start", "AgeStart", "Start")),
        ageEnd: cellNumber(readColumn(row, "Age End", "AgeEnd", "End")),
        maleExpected: cellNumber(readColumn(row, "Male Expected", "MaleExpected")),
        maleBase: cellNumber(readColumn(row, "Male Base", "MaleBase")),
        femaleExpected: cellNumber(readColumn(row, "Female Expected", "FemaleExpected")),
        femaleBase: cellNumber(readColumn(row, "Female Base", "FemaleBase")),
        compositeExpected: cellNumber(readColumn(row, "Composite Expected", "CompositeExpected")),
        compositeBase: cellNumber(readColumn(row, "Composite Base", "CompositeBase")),
      });
    }
  }
  return bands;
}

/** Parses tab-separated rows pasted from a spreadsheet into band drafts for one relation class. */
export function parsePastedBands(text: string, relationClass: RelationClass): ExpectedDebitsBandDraft[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => line.split(/\t|,\s*/).map((cell) => cellNumber(cell)))
    .filter((cells) => cells.some((cell) => cell !== null))
    .map((cells) => ({
      relationClass,
      ageStart: cells[0] ?? null,
      ageEnd: cells[1] ?? null,
      maleExpected: cells[2] ?? null,
      maleBase: cells[3] ?? null,
      femaleExpected: cells[4] ?? null,
      femaleBase: cells[5] ?? null,
      compositeExpected: cells[6] ?? null,
      compositeBase: cells[7] ?? null,
    }));
}

export function exportModificationsWorkbook(rows: readonly ModificationReportRow[]): void {
  const sheet = utils.json_to_sheet(
    rows.map((row) => ({
      Type: row.kind,
      Condition: row.conditionName,
      Rating: row.ratingText ?? "",
      Detail: row.detail,
      Author: row.authorName,
      Date: row.createdUtc.slice(0, 10),
    })),
  );
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, sheet, "Modifications");
  writeFile(workbook, "report-of-modifications.xlsx");
}
